import React, { useEffect, useMemo } from 'react';
import { TemplateExtended } from '@distribute/shared/api-types/templates';

import { getSortedTemplates } from '../lib';

import { NoTemplatesFound } from './NoTemplatesFound';
import { SORT_ITEMS, TemplatesTab } from '../config';
import { useDispatch, useSelector } from 'react-redux';
import { templatesModel } from '../../../features/templates';
import { useTemplatesActions, useView } from '../hooks';

import { TemplatesGrid, TemplatesList } from './TemplatesView';
import { DeleteTemplateConfirmationModal } from '../../../features/templates/ui/components/DeleteTemplateConfirmationModal';
import { TemplateSettingsModal } from '../../../features/templates/ui/components/TemplateSettingsModal';

import {
  FILTER_TEMPLATE_ITEMS,
  getFilteredTemplatesByOwner,
  getFilteredTemplatesByType,
} from '../../../entities/templates';

type Props = {
  templates: TemplateExtended[];
  type: TemplatesTab;
  ownerFilter: string;
  filter: keyof typeof FILTER_TEMPLATE_ITEMS;
  sort: keyof typeof SORT_ITEMS;
};

export const TemplatesPageList: React.FC<Props> = ({
  templates,
  type,
  ownerFilter,
  filter,
  sort,
}) => {
  const { view } = useView();

  const templatesModalOpen = useSelector(
    templatesModel.selectors.selectTemplatesModalOpen
  );

  const {
    isDeleteTemplateModalOpen,
    isTemplateSettingsModalOpen,
    currentTemplate,
    handleOpenDeleteTemplateModal,
    handleOpenTemplateSettingsModal,
    handleCloseDeleteTemplateModal,
    handleCloseTemplateSettingsModal,
  } = useTemplatesActions();

  const dispatch = useDispatch();

  const currentTemplatesToShow = useMemo(() => {
    let _templates = [...templates];

    if (type === TemplatesTab.TEAM) {
      _templates = getFilteredTemplatesByOwner(templates, ownerFilter);
    }

    _templates = getFilteredTemplatesByType(_templates, filter);

    return getSortedTemplates(_templates, sort);
  }, [filter, ownerFilter, sort, templates, type]);

  useEffect(() => {
    if (!templatesModalOpen) {
      dispatch(
        templatesModel.actions.setRenderedTemplates(
          currentTemplatesToShow || []
        )
      );
    }
  }, [currentTemplatesToShow, templatesModalOpen, dispatch]);

  return (
    <>
      {currentTemplate && (
        <DeleteTemplateConfirmationModal
          isOpen={isDeleteTemplateModalOpen}
          onClose={handleCloseDeleteTemplateModal}
          template={currentTemplate}
        />
      )}
      {currentTemplate && (
        <TemplateSettingsModal
          isOpen={isTemplateSettingsModalOpen}
          onClose={handleCloseTemplateSettingsModal}
          template={currentTemplate}
        />
      )}
      {currentTemplatesToShow.length ? (
        view === 'grid' ? (
          <TemplatesGrid
            templates={currentTemplatesToShow}
            handleOpenDeleteTemplateModal={handleOpenDeleteTemplateModal}
            handleOpenTemplateSettingsModal={handleOpenTemplateSettingsModal}
          />
        ) : (
          <TemplatesList
            templates={currentTemplatesToShow}
            handleOpenDeleteTemplateModal={handleOpenDeleteTemplateModal}
            handleOpenTemplateSettingsModal={handleOpenTemplateSettingsModal}
          />
        )
      ) : (
        <NoTemplatesFound type={type} />
      )}
    </>
  );
};
